// App-side delegate for `@heidi/commerce/data` (D3, ARCFE-977 — the billing
// transfer). The cache surface, the pure trial derivation and the request all
// moved; this file keeps the name and every app-coupled concern.
//
// Three things stayed, each for a stated reason:
//   - the merge of the dev-harness override. The override itself lives in
//     `@heidi/commerce/data`; it is merged ON READ here so a refetch cannot
//     wipe it and clearing it restores real data.
//   - the `enabled` composition (store token + Kinde region + user).
//   - the error toast, and with it the `AxiosError` shape check it depends on.
import { useAuthToken } from '@/hooks/useAuthToken';
import {
  applyBillingDevOverride,
  billingDevOverrideAtom,
  buildQueryUserBillingInfoResponse,
  getCurrentUserBillingInfo,
  invalidateCurrentUserBillingInfo,
  type QueryUserBillingInfoResponse,
  setUserBillingInfoQueryCacheFromBillingInfo as setUserBillingInfoQueryCacheFromBillingInfoCore,
  userBillingInfoQueryOptions,
} from '@heidi/commerce/data';
import { toast } from '@heidi/design-system/toast';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAtomValue } from 'jotai';
import { useQueryKindeProperties } from './useQueryKindeProperties';
import { getCurrentUser, useQueryUser } from './useQueryUser';

// Definition moved to `@heidi/commerce/contracts` (C-series, ARCFE-971).
// import + re-export: this module uses the name locally, and a bare
// `export { X } from '...'` does not bind it here.
import { GET_USER_BILLING_INFO } from '@heidi/commerce/contracts';

export {
  buildQueryUserBillingInfoResponse,
  GET_USER_BILLING_INFO,
  getCurrentUserBillingInfo,
  invalidateCurrentUserBillingInfo,
  type QueryUserBillingInfoResponse,
};

/**
 * The affiliate code is read HERE and passed in. The package cannot call
 * `getCurrentUser()` — that is a workspace `/data` read — so the delegate
 * supplies it and behaviour is unchanged.
 */
export const setUserBillingInfoQueryCacheFromBillingInfo = (
  billingInfo: Parameters<typeof setUserBillingInfoQueryCacheFromBillingInfoCore>[0]
) => {
  const user = getCurrentUser();
  setUserBillingInfoQueryCacheFromBillingInfoCore(
    billingInfo,
    user?.affiliate_signup_code
  );
};

export function useQueryUserBillingInfo<T = QueryUserBillingInfoResponse>(
  select?: (data: QueryUserBillingInfoResponse) => T
) {
  const token = useAuthToken();
  const { data: user } = useQueryUser((data) => ({
    kinde_user_id: data?.kinde_user_id,
    affiliate_code: data?.affiliate_signup_code,
  }));
  const { data: kindeProperties } = useQueryKindeProperties();
  // Dev-tools billing override (null in prod; `applyBillingDevOverride` is
  // also a no-op there). Merged on read — never written to the cache — so a
  // refetch can't wipe it and clearing it restores real data instantly.
  const devOverride = useAtomValue(billingDevOverrideAtom);

  return useQuery({
    ...userBillingInfoQueryOptions({
      enabled: !!token && !!kindeProperties?.region && !!user,
      affiliateCode: user?.affiliate_code,
      onBillingFetchError: (error) => {
        if (error instanceof AxiosError) {
          // !Should handle this logic in query client
          const response = error.response;
          if (!response || !response.status || !response.data) {
            return;
          }
          toast({
            tone: 'destructive',
            title: 'Error',
            description: error.message,
          });
        }
      },
    }),
    select: (data: QueryUserBillingInfoResponse): T => {
      const effective = applyBillingDevOverride(
        data,
        devOverride
      ) as QueryUserBillingInfoResponse;
      return select ? select(effective) : (effective as T);
    },
  });
}
